import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, ChannelBadge } from './inboxIcons';

const INTENT_LABELS = {
  order_status: 'Order status', delay_complaint: 'Delay complaint', not_received: 'Not received',
  damaged_or_wrong: 'Damaged or wrong', colour_mismatch: 'Colour mismatch', return_exchange: 'Return or exchange',
  cancel: 'Cancellation', address_change: 'Address change', refund: 'Refund', payment_issue: 'Payment issue',
  invoice: 'Invoice', wholesale: 'Wholesale', product_or_offer_question: 'Product or offer', international: 'International', other: 'Other',
};
const CHANNEL_OF = { email: 'email', instagram: 'ig', messenger: 'fb', whatsapp: 'wa', contact_form: 'form' };
const USD_TO_INR = 88; // display approximation only

const inr = (usd) => `₹${Math.round(Number(usd || 0) * USD_TO_INR).toLocaleString('en-IN')}`;
const pct = (n, total) => (total ? `${Math.round((n / total) * 1000) / 10}%` : '—');
const waitText = (min) => (min == null ? '—' : min < 60 ? `${Math.round(min)} min` : min < 2880 ? `${Math.round(min / 60)} h` : `${Math.round(min / 1440)} days`);

function ago(msVal) {
  if (!msVal) return '';
  const min = Math.round((Date.now() - msVal) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  if (min < 1440) return `${Math.round(min / 60)}h ago`;
  return `${Math.round(min / 1440)}d ago`;
}

function clock(msVal) {
  if (!msVal) return '';
  const d = new Date(msVal);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function activityText(a) {
  if (a.error) return <>Error: {a.error}</>;
  if (a.kind === 'spam') return 'Skipped as spam';
  if (a.kind === 'vendor_or_system') return 'Skipped as system or vendor mail';
  const status = { pending: 'draft waiting', sent: 'sent', rejected: 'rejected', superseded: 'replaced by a newer draft' }[a.draftStatus] || a.draftStatus || 'drafted';
  return <>Drafted <b>{INTENT_LABELS[a.intent] || a.intent || 'reply'}</b> · {status}{a.humanPriority === 'high' ? ' · flagged urgent' : ''}</>;
}

// ---- Backlog review: one pass over every open ticket ----
const GROUPS = [
  { id: 'urgent', title: 'Urgent: draft written, needs attention', tone: 'high', preselect: false },
  { id: 'reply', title: 'Reply: draft written, review and send', tone: 'normal', preselect: false },
  { id: 'close_delivered', title: 'Close: order since delivered, no message sent', tone: '', preselect: true },
  { id: 'close_spam', title: 'Close: spam, system or vendor mail', tone: '', preselect: true },
];

function BacklogPanel({ api, canRun, onOpenTicket }) {
  const [review, setReview] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // 'start' | 'close'
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api('/api/v1/support/agent/backlog');
      setReview(data.review);
      const pre = new Set();
      (data.review?.items || []).forEach((it) => {
        if (!it.action && GROUPS.find((g) => g.id === it.suggestion)?.preselect) pre.add(it.ticketId);
      });
      setSelected(pre);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoaded(true);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const runBatches = useCallback(async (runId) => {
    setRunning(true);
    setError(null);
    const wait = (msVal) => new Promise((r) => setTimeout(r, msVal));
    let failures = 0;
    try {
      for (;;) {
        let r;
        try {
          r = await api(`/api/v1/support/agent/backlog/${runId}/next`, 'POST');
          failures = 0;
        } catch (err) {
          // A slow batch can time out in the browser while the server finishes it
          failures += 1;
          if (failures > 4) throw err;
          await wait(8000);
          continue;
        }
        setProgress({ processed: r.processed, total: r.total });
        if (r.status === 'busy') { await wait(5000); continue; }
        if (r.status !== 'running') break;
      }
    } catch (err) {
      setError(`${err.message}. Click Continue review to resume.`);
    } finally {
      setRunning(false);
      await load();
    }
  }, [api, load]);

  const start = async () => {
    setConfirm(null);
    try {
      const r = await api('/api/v1/support/agent/backlog/start', 'POST');
      setProgress({ processed: 0, total: r.total });
      await runBatches(r.runId);
    } catch (err) {
      setError(err.message);
    }
  };

  const closeSelected = async () => {
    setConfirm(null);
    setClosing(true);
    try {
      await api(`/api/v1/support/agent/backlog/${review.runId}/close`, 'POST', { ticketIds: [...selected] });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setClosing(false);
    }
  };

  const grouped = useMemo(() => {
    const out = {};
    (review?.items || []).forEach((it) => { (out[it.suggestion] = out[it.suggestion] || []).push(it); });
    Object.values(out).forEach((list) => list.sort((a, b) => (a.lastInboundMs || 0) - (b.lastInboundMs || 0)));
    return out;
  }, [review]);

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleGroup = (items, on) => setSelected((prev) => {
    const n = new Set(prev);
    items.filter((it) => !it.action).forEach((it) => (on ? n.add(it.ticketId) : n.delete(it.ticketId)));
    return n;
  });

  if (!loaded) return null;
  const isRunning = running || review?.status === 'running';
  const done = progress?.processed ?? review?.processed ?? 0;
  const total = progress?.total ?? review?.total ?? 0;

  return (
    <section className="cx-panel" aria-labelledby="cx-backlog-h">
      <div className="cx-panel-h">
        <div>
          <h2 id="cx-backlog-h">Backlog review</h2>
          <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>One pass over every open ticket. Nothing is sent or closed until you click.</span>
        </div>
        <div className="cx-page-actions">
          {canRun && !isRunning && <button className="cx-btn" onClick={() => setConfirm('start')}><Icon name="refresh" size={16} />{review ? 'Run a new review' : 'Review all open tickets'}</button>}
          {canRun && !running && review?.status === 'running' && <button className="cx-btn cx-btn-primary" onClick={() => runBatches(review.runId)}>Continue review</button>}
        </div>
      </div>
      <div className="cx-panel-b" style={{ display: 'grid', gap: 12 }}>
        {confirm === 'start' && (
          <div className="cx-confirm">
            The agent writes drafts where a reply is needed and suggests which tickets to close. Nothing is sent. Cost is roughly ₹100.
            <span className="cx-spacer" />
            <button className="cx-btn cx-btn-primary cx-btn-sm" onClick={start}>Start review</button>
            <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => setConfirm(null)}>Cancel</button>
          </div>
        )}
        {isRunning && (
          <div style={{ display: 'grid', gap: 6 }}>
            <div className="cx-progress"><i style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
            <span className="cx-hint">Reviewing {done} of {total} tickets{running ? '…' : ' (paused)'}</span>
          </div>
        )}
        {error && <div className="cx-err">{error}</div>}
        {!review && !isRunning && <span className="cx-hint">No review yet.</span>}
        {review?.status === 'done' && <span className="cx-hint">Reviewed {review.total} tickets · cost {inr(review.costUsd)}</span>}
      </div>
      {review?.status === 'done' && GROUPS.map((g) => {
        const items = grouped[g.id] || [];
        if (!items.length) return null;
        const open = items.filter((it) => !it.action);
        return (
          <div key={g.id}>
            <label className="cx-group-h">
              <input type="checkbox" className="cx-check" checked={open.length > 0 && open.every((it) => selected.has(it.ticketId))} onChange={(e) => toggleGroup(items, e.target.checked)} disabled={!open.length || !canRun} />
              <span className={`cx-dotmark ${g.tone}`} />
              {g.title}<span className="cx-num" style={{ color: 'var(--ink-3)' }}>{items.length}</span>
            </label>
            {items.map((it) => (
              <div key={it.ticketId} className="cx-need" style={{ opacity: it.action ? 0.55 : 1 }}>
                <input type="checkbox" className="cx-check" checked={selected.has(it.ticketId)} onChange={() => toggle(it.ticketId)} disabled={!!it.action || !canRun} aria-label={`Select ${it.ticketNumber}`} />
                <div className="cx-need-main">
                  <div>
                    <b className="cx-mono" style={{ fontWeight: 500 }}>{it.ticketNumber}</b>
                    <span>{it.customerName || 'Customer'}</span>
                    {it.angry && <span className="cx-chip cx-c-new">Upset</span>}
                    {it.orderNumber && <span className="cx-chip cx-c-case cx-mono">#{it.orderNumber} {it.orderStage}</span>}
                    {it.action && <span className="cx-chip cx-c-resolved">{it.action}</span>}
                    <span className="cx-hint">{ago(it.lastInboundMs)}</span>
                  </div>
                  <div>{it.reason || it.summary || it.subject}</div>
                </div>
                {onOpenTicket && <button className="cx-btn cx-btn-sm" onClick={() => onOpenTicket(it.ticketId)}>{it.draftId ? 'Open draft' : 'Open'}</button>}
              </div>
            ))}
          </div>
        );
      })}
      {review?.status === 'done' && canRun && (
        <div className="cx-panel-b" style={{ borderTop: '1px solid var(--line)', display: 'grid', gap: 10 }}>
          {confirm === 'close' ? (
            <div className="cx-confirm">
              Close {selected.size} ticket{selected.size === 1 ? '' : 's'}? Customers are not emailed.
              <span className="cx-spacer" />
              <button className="cx-btn cx-btn-primary cx-btn-sm" onClick={closeSelected}>Close tickets</button>
              <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => setConfirm(null)}>Cancel</button>
            </div>
          ) : (
            <div className="cx-actions" style={{ marginTop: 0 }}>
              <button className="cx-btn cx-btn-primary" onClick={() => setConfirm('close')} disabled={closing || selected.size === 0}>
                <Icon name="check" size={16} />{closing ? 'Closing…' : `Close selected (${selected.size}), no message`}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---- Lessons: what the agent has learned from the team's corrections ----
const TOPIC_LABEL = (t) => (t === 'general' ? 'All messages' : INTENT_LABELS[t] || t);
const LESSON_TOPICS = ['general', ...Object.keys(INTENT_LABELS)];

function LessonRow({ l, canEdit, onSave, busy }) {
  const [editing, setEditing] = useState(false);
  const [rule, setRule] = useState(l.rule);
  const [topic, setTopic] = useState(l.topic);
  const save = (status) => onSave(l.id, { ...(editing ? { rule, topic } : {}), ...(status ? { status } : {}) }).then((ok) => ok && setEditing(false));
  return (
    <div className="cx-lesson">
      <div className="cx-lesson-main">
        {editing ? (
          <>
            <textarea className="cx-textarea" rows={3} value={rule} onChange={(e) => setRule(e.target.value)} aria-label="Lesson text" />
            <span className="cx-select" style={{ marginTop: 6 }}>
              <select value={topic} onChange={(e) => setTopic(e.target.value)} aria-label="Applies to">
                {LESSON_TOPICS.map((t) => <option key={t} value={t}>{TOPIC_LABEL(t)}</option>)}
              </select>
              <Icon name="down" />
            </span>
          </>
        ) : (
          <>
            <div className="cx-lesson-rule">{l.rule}</div>
            <div className="cx-lesson-meta">
              <span className="cx-chip cx-c-plain">{TOPIC_LABEL(l.topic)}</span>
              {l.why && <span>{l.why}</span>}
              {l.feedbackCount > 0 && <span>· from {l.feedbackCount} correction{l.feedbackCount === 1 ? '' : 's'}</span>}
              {l.replacesLessonId && <span className="cx-chip cx-c-waiting">Updates an older lesson</span>}
              {l.status === 'active' && l.approvedByName && <span>· approved by {l.approvedByName}</span>}
            </div>
          </>
        )}
      </div>
      {canEdit && (
        <div className="cx-lesson-actions">
          {editing ? (
            <>
              <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={busy} onClick={() => save(l.status === 'suggested' ? 'active' : null)}>{l.status === 'suggested' ? 'Save and approve' : 'Save'}</button>
              <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => { setEditing(false); setRule(l.rule); setTopic(l.topic); }}>Cancel</button>
            </>
          ) : l.status === 'suggested' ? (
            <>
              <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={busy} onClick={() => save('active')}><Icon name="check" size={14} />Approve</button>
              <button className="cx-btn cx-btn-sm" disabled={busy} onClick={() => setEditing(true)}><Icon name="edit" size={14} />Edit</button>
              <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => save('dismissed')}>Dismiss</button>
            </>
          ) : l.status === 'active' ? (
            <>
              <button className="cx-btn cx-btn-sm" disabled={busy} onClick={() => setEditing(true)}><Icon name="edit" size={14} />Edit</button>
              <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => save('off')}>Turn off</button>
            </>
          ) : (
            <button className="cx-btn cx-btn-sm" disabled={busy} onClick={() => save('active')}>Turn on</button>
          )}
        </div>
      )}
    </div>
  );
}

function LessonsPanel({ api, canEdit }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showOff, setShowOff] = useState(false);
  const [adding, setAdding] = useState(null); // { rule, topic }

  const load = useCallback(() => api('/api/v1/support/agent/lessons').then(setData).catch((e) => setMsg(e.message)), [api]);
  useEffect(() => { load(); }, [load]);

  const save = async (id, body) => {
    setBusy(true);
    setMsg(null);
    try {
      await api(`/api/v1/support/agent/lessons/${id}`, 'PATCH', body);
      await load();
      return true;
    } catch (e) {
      setMsg(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const suggest = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api('/api/v1/support/agent/lessons/suggest', 'POST');
      setMsg(r.feedback ? `Reviewed ${r.feedback} correction${r.feedback === 1 ? '' : 's'} · ${r.proposed} new lesson${r.proposed === 1 ? '' : 's'} to review` : 'No new corrections to learn from yet.');
      await load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    setBusy(true);
    try {
      await api('/api/v1/support/agent/lessons', 'POST', adding);
      setAdding(null);
      await load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return msg ? <section className="cx-panel"><div className="cx-panel-b cx-err">{msg}</div></section> : null;
  const suggested = data.lessons.filter((l) => l.status === 'suggested');
  const active = data.lessons.filter((l) => l.status === 'active');
  const off = data.lessons.filter((l) => l.status === 'off' || l.status === 'dismissed');
  const weeks = data.trend.overall;
  const withData = weeks.filter((w) => w.decided);

  return (
    <section className="cx-panel" aria-labelledby="cx-lessons-h">
      <div className="cx-panel-h">
        <div>
          <h2 id="cx-lessons-h">Lessons</h2>
          <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>What the agent learns from your redrafts, edits, rejections and own replies. Only approved lessons are used.</span>
        </div>
        {canEdit && (
          <div className="cx-page-actions">
            <button className="cx-btn" onClick={() => setAdding({ rule: '', topic: 'general' })} disabled={busy}><Icon name="plus" size={16} />Add lesson</button>
            <button className="cx-btn" onClick={suggest} disabled={busy} title="Normally runs every night at 9 PM"><Icon name="spark" size={16} />{busy ? 'Working…' : `Learn now${data.pendingFeedback ? ` (${data.pendingFeedback} new)` : ''}`}</button>
          </div>
        )}
      </div>
      {msg && <div className="cx-panel-b cx-hint" style={{ paddingBottom: 0 }}>{msg}</div>}
      {adding && (
        <div className="cx-panel-b" style={{ display: 'grid', gap: 8 }}>
          <textarea className="cx-textarea" rows={2} value={adding.rule} onChange={(e) => setAdding({ ...adding, rule: e.target.value })} placeholder="e.g. Always mention that every fabric is printed fresh when explaining dispatch time." aria-label="New lesson" />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="cx-select"><select value={adding.topic} onChange={(e) => setAdding({ ...adding, topic: e.target.value })} aria-label="Applies to">{LESSON_TOPICS.map((t) => <option key={t} value={t}>{TOPIC_LABEL(t)}</option>)}</select><Icon name="down" /></span>
            <span className="cx-spacer" />
            <button className="cx-btn cx-btn-ghost" onClick={() => setAdding(null)}>Cancel</button>
            <button className="cx-btn cx-btn-primary" onClick={add} disabled={busy || !adding.rule.trim()}>Add and use</button>
          </div>
        </div>
      )}

      {suggested.length > 0 && (
        <>
          <div className="cx-group-h"><span className="cx-dotmark normal" />Waiting for your approval<span className="cx-num" style={{ color: 'var(--ink-3)' }}>{suggested.length}</span></div>
          {suggested.map((l) => <LessonRow key={l.id} l={l} canEdit={canEdit} onSave={save} busy={busy} />)}
        </>
      )}
      <div className="cx-group-h"><span className="cx-dotmark" style={{ background: 'var(--done-fg)' }} />In use<span className="cx-num" style={{ color: 'var(--ink-3)' }}>{active.length}</span></div>
      {active.length === 0 ? (
        <div className="cx-panel-b cx-hint">No lessons in use yet. {data.pendingFeedback ? `${data.pendingFeedback} correction${data.pendingFeedback === 1 ? ' is' : 's are'} waiting to be learned from.` : 'Redraft or edit drafts and the agent will suggest lessons each night.'}</div>
      ) : active.map((l) => <LessonRow key={l.id} l={l} canEdit={canEdit} onSave={save} busy={busy} />)}
      {off.length > 0 && (
        <>
          <button className="cx-group-h cx-group-btn" onClick={() => setShowOff((v) => !v)} aria-expanded={showOff}><Icon name="down" size={14} className={showOff ? '' : 'cx-rot-90'} />Turned off or dismissed<span className="cx-num" style={{ color: 'var(--ink-3)' }}>{off.length}</span></button>
          {showOff && off.map((l) => <LessonRow key={l.id} l={l} canEdit={canEdit} onSave={save} busy={busy} />)}
        </>
      )}

      <div className="cx-group-h">Is it improving?</div>
      <div className="cx-panel-b" style={{ display: 'grid', gap: 14 }}>
        {withData.length === 0 ? <span className="cx-hint">The trend appears once drafts have been reviewed.</span> : (
          <>
            <div className="cx-trend" role="img" aria-label="Share of drafts sent as written, per week">
              {weeks.map((w) => {
                const p = w.decided ? Math.round((w.asIs / w.decided) * 100) : null;
                return (
                  <div key={w.weeksAgo} title={w.decided ? `${p}% sent as written · ${w.decided} reviewed` : 'No reviews this week'}>
                    <em>{p == null ? '' : `${p}%`}</em>
                    <i style={{ height: `${p || 0}%`, opacity: p == null ? 0 : 1 }} />
                    <span>{w.weeksAgo === 0 ? 'This wk' : `${w.weeksAgo}w`}</span>
                  </div>
                );
              })}
            </div>
            <table className="cx-table" style={{ border: '1px solid var(--line)', borderRadius: 8 }}>
              <thead><tr><th>Topic</th><th className="r">Reviewed</th><th className="r">Sent as written</th><th className="r">Last 2 weeks</th></tr></thead>
              <tbody>
                {data.trend.topics.slice(0, 8).map((t) => (
                  <tr key={t.topic}>
                    <td>{TOPIC_LABEL(t.topic)}</td>
                    <td className="r cx-num">{t.decided}</td>
                    <td className="r cx-num">{pct(t.asIs, t.decided)}</td>
                    <td className="r cx-num">{t.recentDecided ? pct(t.recentAsIs, t.recentDecided) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </section>
  );
}

// ---- Business facts the agent may state as true ----
export function FactsPanel({ api, canEdit }) {
  const [sections, setSections] = useState(null);
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    api('/api/v1/support/agent/facts').then((d) => setSections(d.sections || [])).catch(() => setSections(null));
  }, [api]);

  const update = (i, key, value) => {
    setSections((prev) => prev.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)));
    setDirty(true);
    setMessage(null);
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const d = await api('/api/v1/support/agent/facts', 'PUT', { sections });
      setSections(d.sections);
      setDirty(false);
      setMessage('Saved. The agent uses these from its next reply.');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!sections) return null;
  return (
    <section className="cx-panel" aria-labelledby="cx-facts-h">
      <div className="cx-panel-h">
        <div>
          <h2 id="cx-facts-h">Business facts</h2>
          <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>What the agent may tell customers as fact. Anything not here, it hands to the team.</span>
        </div>
        <button className="cx-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Hide' : `${canEdit ? 'View and edit' : 'View'} (${sections.length})`}</button>
      </div>
      {open && (
        <div className="cx-panel-b cx-facts">
          {sections.map((s, i) => (
            <div key={s.id} className="cx-fact">
              {canEdit ? (
                <>
                  <div className="cx-fact-top">
                    <input className="cx-input" value={s.title} onChange={(e) => update(i, 'title', e.target.value)} aria-label="Section title" />
                    {removing === s.id ? (
                      <>
                        <button className="cx-btn cx-btn-sm" onClick={() => { setSections((prev) => prev.filter((_, idx) => idx !== i)); setDirty(true); setRemoving(null); }}>Remove</button>
                        <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => setRemoving(null)}>Keep</button>
                      </>
                    ) : (
                      <button className="cx-btn cx-btn-ghost cx-btn-icon" onClick={() => setRemoving(s.id)} aria-label={`Remove ${s.title}`}><Icon name="x" size={16} /></button>
                    )}
                  </div>
                  <textarea className="cx-textarea" rows={Math.min(16, Math.max(4, String(s.body || '').split('\n').length + 1))} value={s.body} onChange={(e) => update(i, 'body', e.target.value)} aria-label={`${s.title} facts`} />
                </>
              ) : (
                <>
                  <b>{s.title}</b>
                  <div className="cx-fact-ro">{s.body}</div>
                </>
              )}
            </div>
          ))}
          {canEdit && (
            <div className="cx-actions" style={{ marginTop: 0, alignItems: 'center' }}>
              {message && <span className={message.startsWith('Saved') ? 'cx-hint' : 'cx-err'}>{message}</span>}
              <button className="cx-btn" onClick={() => { setSections((prev) => [...prev, { id: `custom_${Date.now()}`, title: 'New section', body: '' }]); setDirty(true); }}><Icon name="plus" size={16} />Add section</button>
              <button className="cx-btn cx-btn-primary" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save facts'}</button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---- On/off switch with today's spend ----
export function AgentSwitch({ api, onChange }) {
  const [state, setState] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api('/api/v1/support/agent/config').then(setState).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  if (!state) return null;
  const on = !!state.config?.enabled;

  const flip = async () => {
    if (!on && !confirm) { setConfirm(true); return; }
    setConfirm(false);
    setBusy(true);
    try {
      await api('/api/v1/support/agent/config', 'PUT', { enabled: !on });
      await load();
      onChange?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 8, justifyItems: 'end' }}>
      <span className="cx-switch">
        AI agent {on ? 'on' : 'off'}
        <button role="switch" aria-checked={on} aria-label="AI agent" onClick={flip} disabled={busy} />
      </span>
      {confirm && (
        <div className="cx-confirm">
          It drafts replies for new customer messages. Nothing is sent without approval.
          <button className="cx-btn cx-btn-primary cx-btn-sm" onClick={flip}>Turn on</button>
          <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => setConfirm(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

function AgentPage({ api, isAdmin, onOpenTicket }) {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([api('/api/v1/support/agent/overview'), api('/api/v1/support/agent/config').catch(() => null)]);
      setData(o);
      setConfig(c);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (error && !data) {
    return <div className="cx-page"><div className="cx-empty"><strong>Couldn't load the AI agent</strong>{error}<div style={{ marginTop: 12 }}><button className="cx-btn" onClick={load}>Try again</button></div></div></div>;
  }
  if (!data) {
    return <div className="cx-page" aria-busy="true"><div className="cx-page-in"><div className="cx-skel" style={{ height: 48, margin: '0 0 20px' }} /><div className="cx-skel" style={{ height: 96, margin: 0 }} /><div className="cx-skel" style={{ height: 240, margin: '20px 0 0' }} /></div></div>;
  }

  const { model, today, week, scorecard, needsYou, activity, pendingRegistrations } = data;
  const intents = Object.entries(scorecard.byIntent || {}).sort((a, b) => b[1].total - a[1].total);
  const capUsd = Number(config?.config?.dailyBudgetUsd || 0);
  const spentUsd = Number(config?.todayUsage?.costUsd ?? today.costUsd ?? 0);

  return (
    <div className="cx-page">
      <div className="cx-page-in">
        <div className="cx-page-head">
          <div>
            <h1>AI agent</h1>
            <p className="cx-lede">Drafts replies for every new message on email, Instagram and Messenger, around the clock. Nothing is sent until a team member approves it. Model: {model.draft} on {model.provider === 'vertex-gemini' ? 'Google Vertex AI' : model.provider}.</p>
          </div>
          <div className="cx-page-actions" style={{ alignItems: 'flex-start' }}>
            {isAdmin && <AgentSwitch api={api} onChange={load} />}
            <button className="cx-btn" onClick={load} disabled={loading}><Icon name="refresh" size={16} />{loading ? 'Refreshing…' : 'Refresh'}</button>
          </div>
        </div>

        <div className="cx-metrics">
          <div className="cx-metric"><span>Drafts today</span><b>{today.drafted}</b><small>{today.skipped} spam and system mails skipped · {week.drafted} this week</small></div>
          <div className="cx-metric"><span>Sent as written</span><b>{pct(scorecard.sentAsIs, scorecard.decided)}</b><small>{scorecard.decided} reviewed · {scorecard.pending} waiting</small></div>
          <div className="cx-metric"><span>Median customer wait</span><b>{waitText(scorecard.medianWaitMin)}</b><small>message in to reply sent</small></div>
          <div className="cx-metric">
            <span>Spend today</span><b>{inr(spentUsd)}</b>
            <small>{capUsd ? `daily cap ${inr(capUsd)}` : 'no daily cap set'} · {inr(week.costUsd)} this week</small>
            {capUsd > 0 && <div className="cx-bar"><i style={{ width: `${Math.min(100, (spentUsd / capUsd) * 100)}%` }} /></div>}
          </div>
        </div>

        <div className="cx-two">
          <section className="cx-panel" aria-labelledby="cx-needs-h">
            <div className="cx-panel-h"><h2 id="cx-needs-h">Needs you now</h2><span>{needsYou.length} waiting</span></div>
            {needsYou.length === 0 ? <div className="cx-empty" style={{ padding: 32 }}><strong>Nothing waiting</strong>New drafts appear here as messages come in.</div> : (
              <div>
                {needsYou.map((n) => (
                  <div key={n.draftId} className="cx-need">
                    <span className={`cx-dotmark ${n.humanPriority === 'high' ? 'high' : n.needsHuman ? 'normal' : ''}`} />
                    <div className="cx-need-main">
                      <div>
                        <ChannelBadge channel={CHANNEL_OF[n.channel] || 'email'} />
                        <b className="cx-mono" style={{ fontWeight: 500 }}>{n.ticketNumber}</b>
                        <span>{INTENT_LABELS[n.intent] || n.intent || 'Reply'}</span>
                        {n.angry && <span className="cx-chip cx-c-new">Upset</span>}
                        <span className="cx-hint">{ago(n.createdAtMs)}</span>
                      </div>
                      <div>{n.needsHuman ? n.humanAction : (n.summary || 'Draft ready to review and send')}</div>
                    </div>
                    {onOpenTicket && <button className="cx-btn cx-btn-sm" onClick={() => onOpenTicket(n.ticketId)}>Open</button>}
                  </div>
                ))}
              </div>
            )}
          </section>
          <section className="cx-panel" aria-labelledby="cx-act-h">
            <div className="cx-panel-h"><h2 id="cx-act-h">Activity</h2><span>latest {activity.length}</span></div>
            {activity.length === 0 ? <div className="cx-empty" style={{ padding: 32 }}><strong>No activity yet</strong>Turn the agent on and it starts with the next customer message.</div> : (
              <div className="cx-feed" style={{ maxHeight: 420, overflowY: 'auto' }}>
                {activity.map((a) => (
                  <div key={`${a.ticketId}-${a.atMs}`}>
                    <time>{clock(a.atMs)}</time>
                    <p>
                      {onOpenTicket ? <button className="cx-linkbtn" onClick={() => onOpenTicket(a.ticketId)}>{a.ticketNumber}</button> : <span className="cx-mono">{a.ticketNumber}</span>}{' '}
                      <span style={a.error ? { color: 'var(--new-fg)' } : undefined}>{activityText(a)}</span>
                      {a.summary && <><br />{a.summary}</>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className={`cx-two${pendingRegistrations ? '' : ' even'}`}>
          <section className="cx-panel" aria-labelledby="cx-q-h">
            <div className="cx-panel-h"><h2 id="cx-q-h">Quality by topic</h2><span>share sent without edits</span></div>
            {intents.length === 0 ? <div className="cx-empty" style={{ padding: 32 }}><strong>No reviewed drafts yet</strong>Scores build up as the team approves, edits or rejects drafts.</div> : (
              <>
                <div style={{ paddingTop: 8 }}>
                  {intents.map(([k, v]) => {
                    const p = v.total ? Math.round((v.asIs / v.total) * 1000) / 10 : 0;
                    return (
                      <div key={k} className="cx-qrow" title={`${v.asIs} as written · ${v.edited} corrected (edited, redrafted or replaced) · ${v.rejected} rejected`}>
                        <span>{INTENT_LABELS[k] || k}</span>
                        <span className="cx-num">{v.total}</span>
                        <div className="cx-qbar"><i style={{ width: `${p}%` }} /><em>{p}%</em></div>
                      </div>
                    );
                  })}
                </div>
                <div className="cx-panel-note">Topics that stay above 85% are good candidates for sending automatically later.</div>
              </>
            )}
          </section>
          {pendingRegistrations && (
            <section className="cx-panel" aria-labelledby="cx-reg-h">
              <div className="cx-panel-h"><h2 id="cx-reg-h">Wholesale registrations waiting</h2><span>{pendingRegistrations.length}</span></div>
              {pendingRegistrations.length === 0 ? <div className="cx-empty" style={{ padding: 32 }}><strong>None waiting</strong>New company requests from Shopify show here.</div> : (
                <div>
                  {pendingRegistrations.map((r) => (
                    <div key={r.id} className="cx-need">
                      <span className={`cx-dotmark ${r.overdue ? 'high' : 'normal'}`} />
                      <div className="cx-need-main">
                        <div><b style={{ fontWeight: 500 }}>{r.company}</b>{r.city && <span className="cx-hint">{r.city}</span>}<span className={`cx-chip ${r.overdue ? 'cx-c-new' : 'cx-c-waiting'}`}>{r.ageHours >= 48 ? `${Math.floor(r.ageHours / 24)} days` : `${r.ageHours} h`}</span></div>
                        <div>{[r.name !== r.company ? r.name : null, r.email, r.phone].filter(Boolean).join(' · ')}</div>
                      </div>
                      {r.adminUrl && <a className="cx-btn cx-btn-sm" href={r.adminUrl} target="_blank" rel="noreferrer"><Icon name="shop" size={14} />Open in Shopify</a>}
                    </div>
                  ))}
                </div>
              )}
              <div className="cx-panel-note">The website promises approval in 24 to 48 hours. Overdue ones are emailed every morning, Monday to Saturday.</div>
            </section>
          )}
        </div>

        <div style={{ marginTop: 20 }}>
          <LessonsPanel api={api} canEdit={isAdmin} />
          <BacklogPanel api={api} canRun={isAdmin} onOpenTicket={onOpenTicket} />
          <FactsPanel api={api} canEdit={isAdmin} />
        </div>
      </div>
    </div>
  );
}

export default AgentPage;
